// pages/CompetencyQuiz/ResultsTab.js
//
// Every submitted attempt (competencyQuiz.results: IT Admin / IT Team). Server-paged
// (Infrastructure/Paging kit, 25 a page); search by name, ID or department; date
// range on the submit date; "Export CSV" walks every page with the same filters.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Input, Row, Spinner, Table } from "reactstrap";
import { toast } from "react-toastify";
import { EmptyState, useDebouncedValue } from "../../Components/Common/DashboardKit";
import { exportAllPagesToCsv } from "../../Components/Common/DashboardKit/exportCsv";
import { errorText, listResults, pick } from "../../services/competencyQuizApi";
import { BandBadge, fmtDateTime, fmtPercent } from "./quizFormat";
import useSingleFlight from "./useSingleFlight";

const PAGE_SIZE = 25;

// The date boxes are the viewer's local calendar days: send the start of "from" and the start
// of the day AFTER "to" as instants, so "to" includes the whole day wherever the server runs.
const dayStartIso = (yyyyMmDd, addDays = 0) => {
    if (!yyyyMmDd) return "";
    const [y, m, d] = yyyyMmDd.split("-").map(Number);
    return new Date(y, m - 1, d + addDays).toISOString();
};

export default function ResultsTab() {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [data, setData] = useState({ items: [], total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const debounced = useDebouncedValue(search, 300);
    const once = useSingleFlight();
    // One state object, so a filter change and the page reset are a single update (one request).
    const [query, setQuery] = useState({ page: 1, search: "", from: "", to: "" });
    const page = query.page;
    const setPage = (p) => setQuery((q) => ({ ...q, page: p }));
    const filterParams = (q) => ({ search: q.search, from: dayStartIso(q.from), to: dayStartIso(q.to, 1) });

    useEffect(() => { setQuery((q) => (q.search === debounced ? q : { ...q, search: debounced, page: 1 })); }, [debounced]);
    const setFrom = (v) => setQuery((q) => ({ ...q, from: v, page: 1 }));
    const setTo = (v) => setQuery((q) => ({ ...q, to: v, page: 1 }));
    const from = query.from;
    const to = query.to;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listResults({ page: query.page, pageSize: PAGE_SIZE, ...filterParams(query) });
            setData({
                items: pick(res, "Items", "items") || [],
                total: pick(res, "Total", "total") || 0,
                totalPages: pick(res, "TotalPages", "totalPages") || 0,
            });
        } catch (err) {
            toast.error(errorText(err, "Could not load the results."));
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => once("export", async () => {
        setExporting(true);
        try {
            const n = await exportAllPagesToCsv({
                filename: "Training_Quiz_Results.csv",
                pageSize: 200,
                // No cached total: walk the pages until the server says there are no more, so rows
                // submitted after the table loaded are included too.
                fetchPage: async (p, size) => {
                    const res = await listResults({ page: p, pageSize: size, ...filterParams(query) });
                    return { rows: pick(res, "Items", "items") || [], total: pick(res, "Total", "total") || 0 };
                },
                columns: [
                    { label: "Emp ID", get: (r) => pick(r, "EmpId", "empId") },
                    { label: "Employee", get: (r) => pick(r, "EmployeeName", "employeeName") || "" },
                    { label: "Department", get: (r) => pick(r, "Department", "department") || "" },
                    { label: "Started", get: (r) => fmtDateTime(pick(r, "StartedAt", "startedAt")) },
                    { label: "Submitted", get: (r) => fmtDateTime(pick(r, "SubmittedAt", "submittedAt")) },
                    { label: "Time ran out", get: (r) => (pick(r, "AutoSubmitted", "autoSubmitted") ? "Yes" : "No") },
                    { label: "Correct", get: (r) => pick(r, "CorrectCount", "correctCount") },
                    { label: "Questions", get: (r) => pick(r, "QuestionCount", "questionCount") },
                    { label: "Score %", get: (r) => pick(r, "ScorePercent", "scorePercent") },
                    { label: "Band", get: (r) => pick(r, "Band", "band") || "" },
                    { label: "Critical misses", get: (r) => pick(r, "CriticalMisses", "criticalMisses") },
                ],
            });
            toast.success(`Exported ${n} result${n === 1 ? "" : "s"}.`);
        } catch (err) {
            toast.error(errorText(err, "Export failed."));
        } finally {
            setExporting(false);
        }
    });

    return (
        <>
            <Row className="g-2 align-items-end mb-3">
                <Col md={4}>
                    <Input placeholder="Search name, ID or department" value={search} onChange={(e) => setSearch(e.target.value)} />
                </Col>
                <Col xs={6} md={2}>
                    <label className="form-label small text-muted mb-1">Submitted from</label>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </Col>
                <Col xs={6} md={2}>
                    <label className="form-label small text-muted mb-1">to</label>
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </Col>
                <Col md={4} className="text-md-end">
                    <Button color="light" onClick={exportCsv} disabled={exporting || data.total === 0}>
                        {exporting ? <Spinner size="sm" className="me-1" /> : <i className="ri-file-excel-2-line me-1" />}Export CSV
                    </Button>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-5"><Spinner size="sm" /> Loading…</div>
            ) : data.items.length === 0 ? (
                <EmptyState title="No results" message={search || from || to ? "Nothing matches the filters." : "Nobody has submitted the quiz yet."} />
            ) : (
                <>
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Employee</th>
                                    <th>Dept</th>
                                    <th>Submitted</th>
                                    <th className="text-end">Score</th>
                                    <th>Band</th>
                                    <th className="text-end">Critical misses</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((r) => {
                                    const id = pick(r, "AttemptId", "attemptId");
                                    return (
                                        <tr key={id} style={{ cursor: "pointer" }} onClick={() => navigate(`/competency-quiz/result/${id}`)}>
                                            <td>
                                                <div className="fw-semibold">{pick(r, "EmployeeName", "employeeName") || "—"}</div>
                                                <div className="small text-muted">{pick(r, "EmpId", "empId")}</div>
                                            </td>
                                            <td>{pick(r, "Department", "department") || "—"}</td>
                                            <td>
                                                {fmtDateTime(pick(r, "SubmittedAt", "submittedAt"))}
                                                {pick(r, "AutoSubmitted", "autoSubmitted") && <div className="small text-muted">time ran out</div>}
                                            </td>
                                            <td className="text-end">
                                                {pick(r, "CorrectCount", "correctCount")}/{pick(r, "QuestionCount", "questionCount")} · {fmtPercent(pick(r, "ScorePercent", "scorePercent"))}
                                            </td>
                                            <td><BandBadge band={pick(r, "Band", "band")} /></td>
                                            <td className="text-end">{pick(r, "CriticalMisses", "criticalMisses")}</td>
                                            <td className="text-end"><i className="ri-arrow-right-s-line text-muted" /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="small text-muted">{data.total} result{data.total === 1 ? "" : "s"}</span>
                        <div className="d-flex gap-2 align-items-center">
                            <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                            <span className="small">Page {page} of {Math.max(1, data.totalPages)}</span>
                            <Button size="sm" color="light" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
